/**
 * Invoice Status Constants
 * 
 * This file contains shared constants for invoice status management
 * to avoid hard-coding status arrays throughout the application.
 */

/**
 * Individual status constants to avoid magic strings
 */
export const INVOICE_STATUSES = {
  UNPROCESSED: 'unprocessed',
  ADDING: 'adding',
  NEW_PRODUCTS: 'new_products',
  IR: 'ir',
  INVOICING: 'invoicing',
  HEAD_OFFICE_STOCK: 'head_office_stock',
  EXPENSE_INVOICES: 'expense_invoices',
  ENTERING_STOCK: 'entering_stock',
  ENTERING_EXPENSE: 'entering_expense',
  CREDIT_NOTE: 'credit_note',
  CREDIT_ENTERING: 'credit_entering',
  COMPLETED: 'completed',
} as const;

export type InvoiceStatusKey = typeof INVOICE_STATUSES[keyof typeof INVOICE_STATUSES];

/**
 * Statuses that require unassignment when transitioning to them
 * Used for separation of duties enforcement
 */
export const UNASSIGN_STATUSES = [
  'new_products', 
  'new_product', 
  'ir', 
  'invoicing', 
  'completed'
] as const;

/**
 * Statuses that require assignment to edit
 * Users must be assigned to the invoice to edit fields in these statuses
 */
export const ASSIGNMENT_REQUIRED_STATUSES = [
  'adding',
  'ir',
  'invoicing',
  'entering_stock'
] as const;

/**
 * Statuses where separation of duties applies
 * Users who moved TO these statuses cannot edit in them (except admins)
 */
export const SEPARATION_OF_DUTIES_STATUSES = [
  'ir',
  'invoicing'
] as const;

/**
 * Statuses where notes field is hidden
 */
export const NOTES_HIDDEN_STATUSES = [
  'unprocessed',
  'adding',
  'new_products',
  'ir',
  'invoicing',
  'head_office_stock'
] as const;

/**
 * Statuses where workflow fields (store, po, so, ir, inv) are hidden
 */
export const WORKFLOW_FIELDS_HIDDEN_STATUSES = [
  'unprocessed'
] as const;

/**
 * Statuses where store field is hidden
 */
export const STORE_FIELD_HIDDEN_STATUSES = [
  'head_office_stock',
  'expense_invoices',
  'entering_expense'
] as const;

/**
 * Statuses where PO/SO fields are hidden
 */
export const PO_SO_HIDDEN_STATUSES = [
  'head_office_stock',
  'expense_invoices',
  'entering_expense',
  'credit_note',
  'credit_entering'
] as const;

/**
 * Statuses where IR field is hidden
 */
export const IR_HIDDEN_STATUSES = [
  'adding',
  'new_products',
  'expense_invoices',
  'entering_expense',
  'credit_note',
  'credit_entering'
] as const;

/**
 * Statuses where INV field is hidden
 */
export const INV_HIDDEN_STATUSES = [
  'adding',
  'new_products',
  'ir',
  'head_office_stock',
  'expense_invoices',
  'entering_expense',
  'credit_note',
  'credit_entering'
] as const;

/**
 * Statuses where notes field is visible and editable (admin only)
 */
export const NOTES_VISIBLE_STATUSES = [
  'expense_invoices',
  'entering_expense',
  'credit_note',
  'credit_entering',
  'entering_stock',
  'completed'
] as const;

/**
 * Statuses that clear previous assignee for fresh assignment cycles
 */
export const CLEAR_PREVIOUS_ASSIGNEE_STATUSES = [
  'new_products',
  'new_product',
  'adding'
] as const;

/**
 * Statuses where workflow fields are editable by stock roles
 */
export const STOCK_EDITABLE_STATUSES = [
  'new_products',
  'ir', 
  'invoicing'
] as const;

/**
 * Statuses where fields are always visible (final states)
 */
export const ALWAYS_VISIBLE_STATUSES = [
  'entering_stock',
  'completed'
] as const;

/**
 * Statuses where admin role is required for field visibility
 */
export const ADMIN_REQUIRED_STATUSES = [
  'head_office_stock',
  'entering_stock', 
  'completed'
] as const;

/**
 * Statuses where basic form fields are editable (non-workflow fields)
 */
export const BASIC_FIELDS_EDITABLE_STATUSES = [
  'unprocessed',
  'adding',
  'new_products'
] as const;

/**
 * Statuses where workflow progression controls are shown
 */
export const WORKFLOW_CONTROLS_VISIBLE_STATUSES = [
  'unprocessed',
  'adding', 
  'new_products',
  'ir',
  'invoicing',
  'head_office_stock',
  'entering_stock',
  'expense_invoices',
  'entering_expense',
  'credit_note',
  'credit_entering'
] as const;

/**
 * Statuses where new stock checkbox is visible
 */
export const NEW_STOCK_CHECKBOX_VISIBLE_STATUSES = [
  'adding'
] as const;

/**
 * Statuses where product added checkbox is visible
 */
export const PRODUCT_ADDED_CHECKBOX_VISIBLE_STATUSES = [
  'new_products'
] as const;

/**
 * Statuses where classification selector is editable
 */
export const CLASSIFICATION_EDITABLE_STATUSES = [
  'unprocessed'
] as const;

/**
 * Final/terminal statuses where no further workflow progression is possible
 */
export const TERMINAL_STATUSES = [
  'completed'
] as const;

/**
 * Status group mappings for complex visibility rules
 */
export const STATUS_GROUPS = {
  // Early workflow statuses
  EARLY_WORKFLOW: ['unprocessed', 'adding', 'new_products'] as const,
  
  // Core processing statuses
  CORE_PROCESSING: ['ir', 'invoicing', 'entering_stock'] as const,
  
  // Specialized workflows
  HEAD_OFFICE: ['head_office_stock', 'entering_stock'] as const,
  EXPENSE: ['expense_invoices', 'entering_expense'] as const, 
  CREDIT_NOTE: ['credit_note', 'credit_entering'] as const,
  
  // Final states
  COMPLETION: ['entering_stock', 'completed'] as const,
} as const;

/**
 * Helper function to check if a status belongs to a group
 */
export const isStatusInGroup = (status: string, group: keyof typeof STATUS_GROUPS): boolean => {
  return (STATUS_GROUPS[group] as readonly string[]).includes(status);
};

/**
 * Helper function to check multiple status conditions
 */
export const statusMatches = (status: string, conditions: readonly string[]): boolean => {
  return conditions.includes(status as any);
};
